<template>
  <div class="fade-in">
    <div class="columns is-full">
      <div class="column">
        <h1 class="title">
          {{ patient.attributes.firstName }} {{ patient.attributes.lastName }}
        </h1>
        <div class="is-pulled-right">
          <router-link
            :to="{
              name: 'patient-queue-list-with-filter',
              params: { filters: true }
            }"
            >&laquo; Back to Patient Queue</router-link
          >
        </div>
      </div>
    </div>

    <div class="tabs is-large mt2 is-centered" v-if="showTabs()">
      <ul>
        <li :class="[{ 'is-active': tab === 'provider' }]">
          <a @click="setTab('provider')">Provider</a>
        </li>
        <li :class="[{ 'is-active': tab === 'patient' }]">
          <a @click="setTab('patient')">Patient</a>
        </li>
      </ul>
    </div>

    <div class="columns">
      <div class="column is-one-third">
        <div class="card">
          <div class="card-content">
            <div class="has-text-right">
              <router-link
                :to="{ name: 'patient-print', params: { id: $props.id } }"
              >
                <i class="fa fa-print has-text-grey-light"></i>
              </router-link>
            </div>

            <div v-if="$can('patient_queue:location|patient_queue:manage')">
              <div
                :class="['dropdown', { 'is-active': showLocationMenu }]"
                @click="toggleLocationMenu"
              >
                <div class="dropdown-trigger">
                  <button class="button">
                    <span>
                      Location: {{ patient.attributes.location }}
                      <i
                        v-if="loadingLocation"
                        class="fa fa-refresh fa-spin has-text-grey-light ml1"
                      ></i>
                    </span>
                    <span class="icon is-small">
                      <i class="fa fa-angle-down"></i>
                    </span>
                  </button>
                </div>
                <div class="dropdown-menu" id="dropdown-menu" role="menu">
                  <div class="dropdown-content">
                    <a
                      class="dropdown-item"
                      v-for="location in locations"
                      :key="location"
                      @click.prevent="changeLocation(location)"
                      >{{ location }}</a
                    >
                  </div>
                </div>
              </div>
            </div>
            <div v-else>Location: {{ patient.attributes.location }}</div>

            <br />
            <br />

            <div
              :class="['dropdown', { 'is-active': showStatusMenu }]"
              @click="toggleStatusMenu"
            >
              <div class="dropdown-trigger">
                <button class="button">
                  <span>
                    Status: {{ statusList[patient.attributes.status] }}
                    <i
                      v-if="loadingStatus"
                      class="fa fa-refresh fa-spin has-text-grey-light ml1"
                    ></i>
                  </span>
                  <span class="icon is-small">
                    <i class="fa fa-angle-down"></i>
                  </span>
                </button>
              </div>
              <div class="dropdown-menu" id="dropdown-menu" role="menu">
                <div class="dropdown-content">
                  <a
                    class="dropdown-item"
                    v-for="(status, key) in statusList"
                    :key="key"
                    @click.prevent="changeStatus(key)"
                    >{{ status }}</a
                  >
                </div>
              </div>
            </div>

            <br />
            <br />

            <strong>Submitted:</strong>
            {{ patient.attributes.createdAt | date }}
            <br />
            <br />

            <div class="is-pulled-right" v-if="editable">
              <i
                class="fa fa-pencil has-text-grey-light pointer"
                @click="toggleDemoEdit"
                v-if="!showEditDemo"
              ></i>
              <i
                class="fa fa-floppy-o pointer"
                @click="saveDemo"
                v-if="showEditDemo"
              ></i>
              <i class="fa fa-refresh fa-spin" v-if="loadingDemo"></i>
            </div>
            <div class="demographics">
              <strong>Demographics</strong>
              <div class="mv1 demo">
                <div class="label">Full Name:</div>
                <div v-if="!showEditDemo">
                  {{ patient.attributes.firstName }}
                  {{ patient.attributes.lastName }}
                </div>
                <div v-if="showEditDemo">
                  <div class="columns">
                    <div class="column">
                      <input
                        type="text"
                        class="input"
                        v-model="model.firstName"
                        required
                        placeholder="First Name"
                        name="firstName"
                      />
                    </div>
                    <div class="column">
                      <input
                        type="text"
                        class="input"
                        v-model="model.lastName"
                        required
                        placeholder="Last Name"
                        name="lastName"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="mv1 demo">
                <div class="label">Email:</div>
                <div v-if="!showEditDemo">
                  {{
                    patient.attributes.email
                      ? patient.attributes.email
                      : placeholder
                  }}
                </div>
                <div v-if="showEditDemo">
                  <div class="columns">
                    <div class="column">
                      <input
                        type="email"
                        class="input"
                        v-model="model.email"
                        placeholder="Email"
                        name="email"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="mv1 demo">
                <div class="label">Phone:</div>
                <div v-if="!showEditDemo">
                  {{
                    patient.attributes.phone
                      ? patient.attributes.phone
                      : placeholder
                  }}
                </div>
                <div v-if="showEditDemo">
                  <div class="columns">
                    <div class="column">
                      <input
                        type="tel"
                        class="input"
                        v-model="model.phone"
                        placeholder="Phone"
                        name="phone"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="mv1 demo">
                <div class="label">City:</div>
                <div v-if="!showEditDemo">
                  {{
                    patient.attributes.city
                      ? patient.attributes.city
                      : placeholder
                  }}
                </div>
                <div v-if="showEditDemo">
                  <div class="columns">
                    <div class="column">
                      <input
                        type="text"
                        class="input"
                        v-model="model.city"
                        placeholder="City"
                        name="City"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="mv1 demo">
                <div class="label">State:</div>
                <div v-if="!showEditDemo">
                  {{
                    patient.attributes.state
                      ? patient.attributes.state
                      : placeholder
                  }}
                </div>
                <div v-if="showEditDemo">
                  <div class="columns">
                    <div class="column">
                      <div class="select">
                        <select name="state" v-model="model.state" required>
                          <option
                            v-for="(value, key) in states"
                            :value="key"
                            :key="key"
                          >
                            {{ value }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mv1 demo">
                <div class="label">Zip:</div>
                <div v-if="!showEditDemo">
                  {{
                    patient.attributes.zip
                      ? patient.attributes.zip
                      : placeholder
                  }}
                </div>
                <div v-if="showEditDemo">
                  <div class="columns">
                    <div class="column">
                      <input
                        type="tel"
                        class="input"
                        v-model="model.zip"
                        placeholder="Zip"
                        name="zip"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="score has-text-centered mt2">
              <strong class="patient-score-title">Patient Score</strong>
              <br />
              <span class="patient-score-total"
                >{{ patient.attributes.score }}%</span
              >
            </div>

            <div class="notes mt2">
              <div>
                <div class="is-pulled-right" v-if="editable">
                  <i
                    class="fa fa-pencil has-text-grey-light pointer"
                    @click="toggleNotesEdit"
                    v-if="!showEditNotes"
                  ></i>
                  <i
                    class="fa fa-floppy-o pointer"
                    @click="saveNotes"
                    v-if="showEditNotes"
                  ></i>
                  <i class="fa fa-refresh fa-spin" v-if="loadingNotes"></i>
                </div>
                <strong class="has-text-grey-light">Notes</strong>
              </div>

              <div v-if="showEditNotes">
                <textarea class="textarea" v-model="notes"></textarea>
              </div>
              <div v-else>{{ patient.attributes.notes }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="column">
        <div class="card">
          <div class="card-content">
            <div class="pull-right" v-if="showCreateVisit()">
              <i
                v-if="!loadingVirtualVisit"
                class="fa fa-video-camera has-text-light pointer"
                @click="createVisit"
              ></i>
              <i
                v-if="loadingVirtualVisit"
                class="fa fa-refresh fa-spin has-text-light"
              ></i>
            </div>
            <h2 class="mb2">Recap</h2>
            <div v-if="patient.attributes.referralDoctor.firstName">
              <h3>Referring Provider:</h3>
              <div v-if="patient.attributes.referralDoctor.referralId">
                {{ patient.attributes.referralDoctor.firstName }}
                {{ patient.attributes.referralDoctor.lastName }}
              </div>
            </div>

            <div v-if="patient.attributes.referral.firstName">
              <h3>Referred By:</h3>
              {{ patient.attributes.referral.firstName }}
              {{ patient.attributes.referral.lastName }}
            </div>

            <div v-if="loadingInterview">
              <i class="fa fa-refresh fa-spin"></i>
            </div>

            <div v-if="!loadingInterview && !interview">
              <h3>No answers were found.</h3>
            </div>

            <div v-if="!loadingInterview && interview">
              <div
                class="interview mb1"
                v-for="question in Object.values(interview)"
                :key="question.id"
              >
                <div class="q">{{ question.attributes.question.title }}</div>
                <div class="a mb1">
                  <div v-if="question.attributes.kind === 'file'">
                    <div class="columns">
                      <div
                        class="column pointer"
                        v-for="image in question.attributes.answer"
                        :key="image.url"
                        @click="showModal(image)"
                      >
                        <img
                          v-if="$isImage(image)"
                          :src="image.url"
                          alt="image"
                          width="150"
                        />
                        <i v-if="!$isImage(image)" class="fa fa-file"></i>
                        <p>{{ image.name }}</p>
                      </div>
                    </div>
                  </div>

                  <div v-else-if="question.attributes.kind === 'instruction'">
                    <div class="columns">
                      <div class="column" v-if="getInstructionName(question)">
                        <h4>Name</h4>
                        {{ getInstructionName(question) }}
                      </div>
                      <div
                        class="column"
                        v-if="getInstructionAcceptedAgreement(question)"
                      >
                        <h4>Accepted Agreement</h4>
                        <label class="accepted-agreement mb1">
                          <input
                            type="checkbox"
                            checked="checked"
                            value="1"
                            disabled="true"
                          />
                          <span
                            v-if="
                              question.attributes.question.meta
                                .customAgreementText
                            "
                            >{{
                              question.attributes.question.meta
                                .customAgreementText
                            }}</span
                          >
                          <span v-else>I Agree</span>
                        </label>
                      </div>
                      <div class="column" v-if="getSignature(question)">
                        <h4>Signature</h4>
                        <img
                          :src="getSignature(question).data"
                          alt="image"
                          width="150"
                        />
                      </div>
                      <div class="column" v-if="getSignatureDate(question)">
                        <h4>Signature Date</h4>
                        <p>{{ getSignatureDate(question) }}</p>
                      </div>
                    </div>
                  </div>

                  <div v-else-if="question.attributes.kind === 'visuwell_user'">
                    <div class="columns">
                      <div class="column">
                        <h4>Organization ID</h4>
                        {{
                          _get(question, "attributes.answer.organizationId") ||
                            "N/A"
                        }}
                        <h4>Organization Location ID</h4>
                        {{
                          _get(
                            question,
                            "attributes.answer.organizationLocationId" || "N/A"
                          )
                        }}
                        <h4>MRN</h4>
                        {{
                          _get(
                            question,
                            "attributes.answer.emrPatientIdentifier" || "N/A"
                          )
                        }}
                        <h4>Last Name</h4>
                        {{
                          _get(question, "attributes.answer.lastName" || "N/A")
                        }}
                        <h4>Date of Birth</h4>
                        {{
                          _get(
                            question,
                            "attributes.answer.dateOfBirth" || "N/A"
                          )
                        }}
                        <h4>Visuwell User ID</h4>
                        {{
                          _get(question, "attributes.answer.userId" || "N/A")
                        }}
                      </div>
                    </div>
                  </div>

                  <div
                    v-else-if="
                      isArray(question.attributes.answer) &&
                        question.attributes.answer.length
                    "
                  >
                    <div v-if="isChoice(question.attributes.answer[0])">
                      <div
                        v-for="choice in question.attributes.answer"
                        :key="choice"
                      >
                        {{ choice.value }}
                      </div>
                    </div>
                    <div v-if="!isChoice(question.attributes.answer[0])">
                      <div
                        v-for="choice in question.attributes.answer"
                        :key="choice"
                      >
                        {{ choice }}
                      </div>
                    </div>
                  </div>

                  <div v-else-if="isObject(question.attributes.answer)">
                    <div
                      v-for="value in values(question.attributes.answer)"
                      :key="value"
                    >
                      {{ value }}
                    </div>
                  </div>

                  <div
                    v-else-if="typeof question.attributes.answer === 'string'"
                  >
                    {{ question.attributes.answer }}
                  </div>

                  <div v-if="!question.attributes.answer">
                    {{ placeholder }}
                  </div>
                </div>
              </div>
            </div>
            <!--Alternate Ending-->
            <div v-if="patient.attributes.interviewAlternateEnding">
              <hr />
              <h2 class="mb2">Ending</h2>
              <h3 class="mb2">
                {{
                  patient.attributes.interviewAlternateEnding
                    .alternateEndingHeader
                }}
              </h3>
              <div>
                <span
                  v-html="
                    patient.attributes.interviewAlternateEnding
                      .alternateEndingMessage
                  "
                ></span>
              </div>

              <div
                v-if="
                  patient.attributes.interviewAlternateEnding
                    .alternateEndingShowOndemandSessionButton
                "
                class="interview mb1"
              >
                <div>
                  <div class="q">On-Demand Message</div>
                  <div
                    class="a"
                    v-html="
                      patient.attributes.interviewAlternateEnding
                        .alternateEndingOnDemandMessage
                    "
                  ></div>
                </div>
                <div>
                  <div class="q">On-Demand Button</div>
                  <div class="a">
                    {{
                      patient.attributes.interviewAlternateEnding
                        .alternateEndingOnDemandButtonText
                    }}
                  </div>
                </div>
              </div>
            </div>

            <!--Regular Success Ending-->
            <div
              v-if="
                !patient.attributes.interviewAlternateEnding &&
                  !patient.attributes.ondemandEnding &&
                  patient.attributes.interview
              "
            >
              <hr />
              <h2 class="mb2">Ending</h2>
              <h3 class="mb2">
                {{ patient.attributes.interview.nextStepsHeader }}
              </h3>
              <div>
                <span v-html="patient.attributes.interview.nextSteps"></span>
              </div>
            </div>

            <!--OnDemand Session Ending-->
            <div
              v-if="
                !patient.attributes.interviewAlternateEnding &&
                  patient.attributes.ondemandEnding
              "
            >
              <hr />
              <h2 class="mb2">
                {{ patient.attributes.ondemandEnding.title }} Ending
              </h2>
              <h3 class="mb2">
                Organization ID:
                {{
                  (patient.attributes.ondemandEnding.fields[0] || {})
                    .organizationLocationId
                }}
              </h3>
              <div>
                <p>Message:</p>
                <span
                  v-html="
                    (patient.attributes.ondemandEnding.fields[0] || {})
                      .ondemandMessage
                  "
                ></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <image-modal :image="modalImage" :show="modalShow" :hide="hideModal" />
  </div>
</template>

<script>
import _ from "lodash";
import PatientQueueService from "@/services/PatientQueueService";
import states from "./states";
import ImageModal from "./ImageModal";

export default {
  name: "patient-edit",
  props: ["id"],
  components: { ImageModal },

  data() {
    return {
      placeholder: "_ _ _ _ _ _ _ _ _ _ _ _ _ _",
      loadingStatus: false,
      loadingInterview: true,
      loadingNotes: false,
      loadingDemo: false,
      showStatusMenu: false,
      showLocationMenu: false,
      showEditNotes: false,
      showEditDemo: false,
      loadingLocation: false,
      loadingVirtualVisit: false,
      notes: "",
      model: {},
      states,
      editable: false,
      tab: "provider",
      modalImage: {},
      modalShow: false
    };
  },

  computed: {
    patient() {
      return this.$store.getters["patientQueue/patient"](this.$props.id);
    },
    interview() {
      return this.$store.getters["patientQueue/interview"];
    },
    locations() {
      return this.$store.getters["practice/locationsArray"];
    },
    statusList() {
      return this.$store.getters["patientQueue/statusList"];
    },
    currentUser() {
      return this.$store.getters["currentUser"];
    }
  },

  watch: {
    patient() {
      this.canEdit();
    },
    id() {
      this.loadInterview();
    }
  },

  mounted() {
    this.$store.dispatch("notPrinting");
    this.loadInterview();
    this.canEdit();
  },

  methods: {
    _get(object, keyPath) {
      return _.get(object, keyPath);
    },
    getSignature(question) {
      return _.get(question, "attributes.answer.signature");
    },
    getSignatureDate(question) {
      return _.get(question, "attributes.answer.signatureDate");
    },
    getInstructionName(question) {
      return _.get(question, "attributes.answer.name");
    },
    getInstructionAcceptedAgreement(question) {
      return _.get(question, "attributes.answer.acceptedAgreement");
    },
    loadInterview() {
      this.loadingInterview = true;
      PatientQueueService.loadPatient(this.$props.id)
        .then(() => {
          PatientQueueService.getInterview(this.$props.id).then(() => {
            this.loadingInterview = false;
          });
        })
        .catch(error => {
          console.log("failed to load patient", error);
        });
    },
    canEdit() {
      this.editable =
        this.patient.attributes.status === "nuw" ||
        this.patient.attributes.status === "pending";
    },
    toggleStatusMenu() {
      this.showStatusMenu = !this.showStatusMenu;
    },
    changeStatus(status) {
      this.loadingStatus = true;
      PatientQueueService.update({ id: this.patient.id, status }).then(() => {
        this.loadingStatus = false;

        let location = this.currentUser.location || "";
        if (
          this.currentUser.roleId == "Practice Staff" &&
          this.currentUser.location != "" &&
          location == ""
        )
          return;

        PatientQueueService.getCounts({ location: location });
      });
    },
    toggleLocationMenu() {
      this.showLocationMenu = !this.showLocationMenu;
    },
    changeLocation(location) {
      this.loadingLocation = true;
      PatientQueueService.update({ id: this.patient.id, location }).then(() => {
        this.loadingLocation = false;
      });
    },
    toggleNotesEdit() {
      this.showEditNotes = !this.showEditNotes;
      this.notes = this.patient.attributes.notes;
    },
    saveNotes() {
      this.loadingNotes = true;
      PatientQueueService.update({
        id: this.patient.id,
        notes: this.notes
      }).then(() => {
        this.loadingNotes = false;
        this.showEditNotes = false;
      });
    },
    toggleDemoEdit() {
      this.showEditDemo = !this.showEditDemo;
      this.model = _.cloneDeep(this.patient.attributes);
    },
    saveDemo() {
      this.loadingDemo = true;
      PatientQueueService.update({ id: this.patient.id, ...this.model }).then(
        () => {
          this.loadingDemo = false;
          this.showEditDemo = false;
        }
      );
    },
    isArray(answer) {
      return _.isArray(answer);
    },
    isObject(answer) {
      return _.isObject(answer) && !_.isArray(answer);
    },
    values(answer) {
      return _.values(answer);
    },
    isChoice(answer) {
      return _.has(answer, "value");
    },
    showCreateVisit() {
      return (
        this.$can("patient_queue:read|patient_queue:manage") &&
        this.patient.attributes.interviewKind === "Referral_Patient"
      );
    },
    createVisit() {
      this.loadingVirtualVisit = true;
      PatientQueueService.createVisit({ id: this.patient.id }).then(() => {
        this.loadingVirtualVisit = false;
        this.$router.push({ name: "encounter-list" });
      });
    },
    showTabs() {
      return (
        this.patient.attributes.referral.referralId ||
        this.patient.attributes.referralDoctor.referralId
      );
    },
    setTab(tab) {
      this.tab = tab;
      if (tab === "provider") {
        this.$router.push({
          name: "patient-edit",
          params: { id: this.patient.attributes.referral.referralId }
        });
      } else {
        this.$router.push({
          name: "patient-edit",
          params: { id: this.patient.attributes.referralDoctor.referralId }
        });
      }
    },
    hideModal() {
      this.modalShow = false;
    },
    showModal(image) {
      this.modalShow = true;
      this.modalImage = image;
    }
  }
};
</script>

<style lang="scss" scoped>
.q {
  font-weight: 600;
}
h3 {
  font-size: 1.29rem;
}
.fa-file {
  font-size: 100px;
  margin-bottom: 10px;
}
</style>
